import React, { useMemo, useState } from "react";
import styles from "./popularProd.module.css";
import { Cpu, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addToCartThunk } from "../../../../redux/cartSlice";
import { setLoginpopup } from "../../../../redux/customerSlice";
import { stripHtmlTags } from "../../../../utils/utils";
import api from "../../../../utils/api";

// Standard packaging / minimum order quantity for a product, so "Add to cart"
// uses a valid default quantity instead of 1 (the backend enforces SPQ multiples).
const spqOf = (item) => {
  const fromInventory = Number(item?.inventory?.minimumMultiplier);
  if (fromInventory > 0) return fromInventory;
  const packaging = item?.additionalResources?.Standard_Packaging_Quantity;
  const fromPackaging = Number(Array.isArray(packaging) ? packaging[0] : packaging);
  return fromPackaging > 0 ? fromPackaging : 1;
};

// Same stock / price rules as the product page (StockCard): a part is only
// purchasable when it has stock, is not flagged out of stock, and has a price.
const stockOf = (item) => Number(item?.inventory?.qtyAvailable) || 0;
const isInStock = (item) => stockOf(item) > 0 && item?.status !== "OUT OF STOCK";
// The card shows the best (lowest) unit price on the price table — the one at
// the largest quantity break, e.g. 5000 pcs → ₹8.
const lowestUnitPrice = (item) => {
  const table = Array.isArray(item?.quantityTable) ? item.quantityTable : [];
  const prices = table.map((row) => Number(row?.unitPrice)).filter((p) => p > 0);
  return prices.length ? Math.min(...prices) : 0;
};
const manufacturerName = (item) => {
  const m = item?.productOverview?.manufacturer ?? item?.manufacturer;
  if (Array.isArray(m)) return m[0]?.name || "";
  return m?.name || (typeof m === "string" ? m : "");
};

const Brand1Slider = ({ prod = [], manufacturer }) => {
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedin = useSelector((state) => state.customer?.isLoggedin);
  // All of the tab's products are shown at once (the grid wraps) — no paging.
  const products = useMemo(() => prod.filter(Boolean), [prod]);

  const openProduct = (item) => {
    const identifier = item?._id || item?.name;
    if (identifier) navigate(`/productCode/${encodeURIComponent(identifier)}`);
  };

  // Add straight to the cart from the showcase. Visitors who are not signed in
  // get the same login prompt the product page uses.
  const handleAddToCart = async (item) => {
    if (!item?._id || busyId) return;
    if (!isLoggedin) {
      dispatch(setLoginpopup(true));
      return;
    }
    const quantity = spqOf(item);
    setBusyId(item._id);
    try {
      await dispatch(addToCartThunk([{ productId: item._id, totalQty: quantity, quantity }]));
    } finally {
      setBusyId(null);
    }
  };

  // Out of stock (or no price): the same availability enquiry the product page sends.
  const handleCheckAvailability = async (item) => {
    if (!item?._id || busyId) return;
    if (!isLoggedin) {
      toast.error("Please login to check availability");
      dispatch(setLoginpopup(true));
      return;
    }
    setBusyId(item._id);
    try {
      const { status } = await api.sendWizardEnquiry({
        partList: [{
          expectedPrice: "",
          manufacturer: manufacturerName(item),
          masterPrice: "",
          productNumber: item?.productOverview?.productNumber?.[0] || item?.name || "",
          quantity: spqOf(item),
          requiredDate: new Date(),
          gst: item?.gst || 0,
        }],
      });
      if (status === 200) toast.success("Enquiry submitted successfully");
    } catch (err) {
      console.log(err);
      toast.error("Something went wrong");
    } finally {
      setBusyId(null);
    }
  };

  if (!products.length) {
    return (
      <div className={styles.emptySelection}>
        <div className={styles.emptyIcon}><Cpu size={24} /></div>
        <div>
          <span className={styles.emptyEyebrow}>Live catalogue</span>
          <h3>Fresh picks are being prepared</h3>
          <p>Our team is updating this manufacturer’s most in-demand components.</p>
        </div>
        <span className={styles.emptyPulse} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={styles.productShowcase}>
      <div className={styles.productGrid} key={manufacturer}>
        {products.map((item, index) => {
          const name = item?.name || "Electronic component";
          const description = stripHtmlTags(Array.isArray(item?.description) ? item.description[0] : item?.description) || "Verified component specifications and sourcing information.";
          const isBusy = busyId === item?._id;
          const stock = stockOf(item);
          const inStock = isInStock(item);
          const spq = spqOf(item);
          const unitPrice = lowestUnitPrice(item);
          const hasPrice = unitPrice > 0;
          const brand = manufacturerName(item);
          // Can only add when the default (SPQ) quantity is actually available.
          const canAddToCart = inStock && hasPrice && spq <= stock;
          return (
            <article className={styles.prodCard} key={item?._id || `${name}-${index}`}>
              <div
                className={`${styles.cardImageContainer} ${styles.clickable}`}
                onClick={() => openProduct(item)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openProduct(item); }}
                aria-label={`Open ${name}`}
              >
                <img src={item?.image} alt={name} className={styles.cardImage} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                <span className={styles.imageFallback}><Cpu size={34} /></span>
              </div>
              <div className={styles.cardBody}>
                {brand && <span className={styles.mfgBadge} title={brand}>{brand}</span>}
                <h3 className={`${styles.cardTitle} ${styles.clickable}`} title={name} onClick={() => openProduct(item)}>{name}</h3>
                <p className={styles.cardDesc} title={description}>{description}</p>
              </div>
              <div className={styles.cardMeta}>
                {inStock
                  ? <span className={styles.stockIn}>{stock.toLocaleString("en-IN")} In Stock</span>
                  : <span className={styles.stockOut}>Out of stock</span>}
                {hasPrice
                  ? <span className={styles.price}>₹{unitPrice.toLocaleString("en-IN", { maximumFractionDigits: 2 })}<small> / pc</small></span>
                  : <span className={styles.priceMuted}>Price on request</span>}
              </div>
              <div className={styles.cardActions}>
                {canAddToCart ? (
                  <button
                    className={styles.addCartBtn}
                    onClick={() => handleAddToCart(item)}
                    disabled={isBusy}
                  >
                    <ShoppingCart size={15} /> {isBusy ? "Adding…" : "Add to cart"}
                  </button>
                ) : (
                  <button
                    className={styles.addCartBtn}
                    onClick={() => handleCheckAvailability(item)}
                    disabled={isBusy}
                  >
                    {isBusy ? "Please wait…" : inStock ? "Request a quote" : "Check availability"}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default Brand1Slider;
